// packages/core/src/lib/poll/pollValidation.ts
// Validation logic for poll data structures
// Extracted from pollStorage.ts for better modularity and maintainability

import type { Poll, FormResponseItem, FormQuestionShape, DateVoteValue } from "./pollTypes";
import { ErrorFactory, logError } from "../error-handling";

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Check if a value is a non-empty array of strings
 */
export function isNonEmptyStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((v) => typeof v === "string" && v.trim().length > 0)
  );
}

/**
 * Determine if a poll is a "date" poll
 * Handles both explicit type="date" and legacy polls without type but with selectedDates
 */
export function isDatePoll(p: Poll): boolean {
  if (p?.type === "date") return true;
  // Legacy: pas de type mais présence de selectedDates
  const dates = p?.settings?.selectedDates;
  return p?.type === undefined && Array.isArray(dates) && dates.length > 0;
}

// ============================================================================
// POLL VALIDATION
// ============================================================================

/**
 * Validate a poll structure based on its type
 * Throws ErrorFactory.validation if invalid
 */
export function validatePoll(poll: Poll): void {
  // Pour les sondages de type "date" (ou legacy sans type mais avec des dates), exiger selectedDates non vide
  if (isDatePoll(poll)) {
    const dates = poll?.settings?.selectedDates;
    if (!isNonEmptyStringArray(dates)) {
      const validationError = ErrorFactory.validation(
        "Invalid date poll: settings.selectedDates must be a non-empty array of strings",
        "Le sondage doit contenir au moins une date valide",
      );

      logError(validationError, {
        component: "pollValidation",
        operation: "validateDatePoll",
      });

      throw validationError;
    }
    return;
  }
  // Pour les sondages de type "form", valider minimalement le titre
  if (poll.type === "form") {
    if (!poll?.title || typeof poll.title !== "string" || !poll.title.trim()) {
      const validationError = ErrorFactory.validation(
        "Invalid form poll: title must be a non-empty string",
        "Le titre du formulaire est obligatoire",
      );

      logError(validationError, {
        component: "pollValidation",
        operation: "validateFormPoll",
      });

      throw validationError;
    }
    return;
  }
  // Pour les sondages de type "availability", valider minimalement le titre
  if (poll.type === "availability") {
    if (!poll?.title || typeof poll.title !== "string" || !poll.title.trim()) {
      const validationError = ErrorFactory.validation(
        "Invalid availability poll: title must be a non-empty string",
        "Le titre du sondage disponibilités est obligatoire",
      );

      logError(validationError, {
        component: "pollValidation",
        operation: "validateAvailabilityPoll",
      });

      throw validationError;
    }
    return;
  }
  // Pour les sondages de type "quizz", valider minimalement le titre
  if (poll.type === "quizz") {
    if (!poll?.title || typeof poll.title !== "string" || !poll.title.trim()) {
      const validationError = ErrorFactory.validation(
        "Invalid quizz poll: title must be a non-empty string",
        "Le titre du quizz est obligatoire",
      );

      logError(validationError, {
        component: "pollValidation",
        operation: "validateQuizzPoll",
      });

      throw validationError;
    }
    return;
  }
}

// ============================================================================
// FORM RESPONSE VALIDATION
// ============================================================================

/**
 * Validate form response items against poll questions schema
 * Throws ErrorFactory.validation if invalid
 */
export function assertValidFormAnswer(poll: Poll, items: FormResponseItem[]): void {
  const qIndex = new Map<string, FormQuestionShape>();
  for (const q of (poll.questions || []) as FormQuestionShape[]) {
    qIndex.set(q.id, q);
  }
  for (const q of qIndex.values()) {
    const it = items.find((i) => i.questionId === q.id);
    // Required
    if (q.required) {
      if (!it) {
        const validationError = ErrorFactory.validation(
          "Missing required answer",
          "Réponse obligatoire manquante",
        );

        logError(validationError, {
          component: "pollValidation",
          operation: "assertValidFormAnswer",
          questionId: q.id,
        });

        throw validationError;
      }
      if (q.kind === "text" && (typeof it.value !== "string" || !it.value.trim())) {
        const validationError = ErrorFactory.validation(
          "Text answer required",
          "Réponse textuelle obligatoire",
        );

        logError(validationError, {
          component: "pollValidation",
          operation: "assertValidFormAnswer",
          questionId: q.id,
        });

        throw validationError;
      }
      if (q.kind === "single" && (typeof it.value !== "string" || !it.value)) {
        const validationError = ErrorFactory.validation(
          "Single choice required",
          "Choix unique obligatoire",
        );

        logError(validationError, {
          component: "pollValidation",
          operation: "assertValidFormAnswer",
          questionId: q.id,
        });

        throw validationError;
      }
      if (q.kind === "multiple") {
        if (!Array.isArray(it.value) || it.value.length === 0) {
          const validationError = ErrorFactory.validation(
            "At least one choice required",
            "Au moins un choix obligatoire",
          );

          logError(validationError, {
            component: "pollValidation",
            operation: "assertValidFormAnswer",
            questionId: q.id,
          });

          throw validationError;
        }
      }
      if (q.kind === "date") {
        // Vérifier que c'est bien un DateVoteValue (tableau d'objets avec date, timeSlots, vote)
        if (!Array.isArray(it.value) || it.value.length === 0) {
          const validationError = ErrorFactory.validation(
            "Date vote required",
            "Vote sur dates obligatoire",
          );

          logError(validationError, {
            component: "pollValidation",
            operation: "assertValidFormAnswer",
            questionId: q.id,
          });

          throw validationError;
        }
        // Vérifier que chaque entrée a la structure attendue
        const dateValue = it.value as DateVoteValue;
        const isValid = dateValue.every(
          (vote) =>
            typeof vote === "object" &&
            vote !== null &&
            typeof vote.date === "string" &&
            Array.isArray(vote.timeSlots) &&
            (vote.vote === "yes" || vote.vote === "no" || vote.vote === "maybe"),
        );
        if (!isValid) {
          const validationError = ErrorFactory.validation(
            "Invalid date vote format",
            "Format de vote sur dates invalide",
          );

          logError(validationError, {
            component: "pollValidation",
            operation: "assertValidFormAnswer",
            questionId: q.id,
          });

          throw validationError;
        }
      }
    }
    // Max choices validation for multiple choice questions
    if (q.kind === "multiple" && q.maxChoices && it) {
      if (Array.isArray(it.value) && it.value.length > q.maxChoices) {
        const validationError = ErrorFactory.validation(
          `Too many choices selected (max: ${q.maxChoices})`,
          `Trop de choix sélectionnés (maximum: ${q.maxChoices})`,
        );

        logError(validationError, {
          component: "pollValidation",
          operation: "assertValidFormAnswer",
          questionId: q.id,
        });

        throw validationError;
      }
    }
  }
}
